import { Dispatch, SetStateAction } from 'react';
import { css } from '@emotion/react';
import { CreateResponse } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { PhoneConfigOrg } from '@wetags/phone-config';
import { ForwardingNumberTypes } from '@frontend/api-forwarding-number';
import { BadgeBanner } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { Button, useModalControl, XIcon, Text } from '@frontend/design-system';
import { trackingId } from '../../tracking';
import { LabelsCard } from '../common/labels-card';
import { SettingsCard } from '../common/settings-card';
import { CallLegsWithModal } from './call-legs-with-modal';
import { NewRingtoneSelector } from './ringtone-selector.new';

export const NewRingSettings = ({
  callGroup,
  callGroupState,
  callForwardingNumbers,
  setCallGroupState,
  onSave,
}: {
  callGroup: CreateResponse;
  callGroupState: CreateResponse;
  callForwardingNumbers: ForwardingNumberTypes.FwdNumberModel[];
  setCallGroupState: Dispatch<SetStateAction<CreateResponse>>;
  onSave?: () => Promise<void>;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'call-groups' });
  const assignDeviceModalProps = useModalControl();

  const [ringtonesEnabled] = PhoneConfigOrg.useWetag('callgroup-ringtones');

  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { selectedGroupId } = useAppScopeStore();

  const isUnify = settingsTenantLocation?.locationType === 'unify';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--fabric-space-gap-md)' }}>
      <SettingsCard
        title={t('Assigned Devices')}
        action={{
          label: t('Assign Device'),
          onClick: () => assignDeviceModalProps.openModal(),
          ref: assignDeviceModalProps.triggerProps.ref,
          trackingId: trackingId({ context: 'setting', feature: 'call-groups', details: 'open-assign-devices' }),
        }}
      >
        <CallLegsWithModal
          callGroup={callGroup}
          callForwardingNumbers={callForwardingNumbers}
          enableDeviceEditing={true}
          tableLayout='separated'
          removeIcon={XIcon}
          externalModalProps={assignDeviceModalProps}
        />
      </SettingsCard>

      {/* Remove this SettingsCard and all its children once Ringtones have been GA for a while. */}
      <SettingsCard title={t('Ringtone')}>
        {ringtonesEnabled ? (
          <BadgeBanner
            styles={css`
              margin-bottom: var(--fabric-space-gap-lg);
            `}
          >
            <BadgeBanner.AnimatedBadge label={t('New')} />
            <div
              css={css`
                display: flex;
                gap: var(--fabric-space-gap-sm);
                align-items: center;
              `}
            >
              <Text size='medium'>
                {t(
                  'The Ringtone setting has moved to the Call Group step within Call Routes. You can now choose a ringtone each time a Call Group is used in a route, giving you the flexibility to set the sound your team hears for specific call flows.'
                )}
              </Text>
            </div>
          </BadgeBanner>
        ) : (
          <NewRingtoneSelector callGroup={callGroupState} setCallGroup={setCallGroupState} />
        )}
      </SettingsCard>

      {/* Work in progress for the below location selector. Using MultiselectField for now but will need to be changed. */}
      {isUnify && !!selectedGroupId && <LabelsCard callGroup={callGroup} setCallGroup={setCallGroupState} />}
      <Button
        css={{ width: 'fit-content' }}
        onClick={onSave}
        trackingId={trackingId({ context: 'setting', feature: 'call-groups', details: 'save' })}
      >
        {t('Save Changes')}
      </Button>
    </div>
  );
};
